(ns hai.notes.data
  "Note mutations behind the detail page and the wall. A save returns the fresh
   note (the shared editor shape) and stores it at the entity ident the detail
   page reads; the wall re-queries its rows and facet counts when mounted,
   since a paged, filtered collection can't be reconciled from one note."
  (:require
   [clojure.string :as str]
   [hai.notes.mutations :as mutations]
   [hai.ui.request :as request]
   [hai.ui.rf-sugar :refer [defevent-fx defsub]]))

(def table-id
  "Collection id of the notes wall. The mutations re-resolve it when mounted."
  ::table)

(defn- note-ident
  [note-uuid]
  [:note/uuid (uuid (str note-uuid))])

(defn- wall-refresh-fx
  [db]
  (cond-> []
    (get-in db [:collections table-id])
    (conj [:dispatch [:table/resolve table-id]]
          [:dispatch [::load-facets]])))

(defevent-fx ::delete [{:keys [db]} note-uuid]
  ;; The caller navigates away immediately; drop the wall's row now too
  ;; (mount-guarded inside :table/remove-row), and resurrect it with a toast if
  ;; the delete fails.
  {:fx [[:dispatch [:table/remove-row table-id :note/uuid note-uuid]]]
   :resolve (assoc (mutations/delete-resolve note-uuid)
                   :on-success-fx (conj (wall-refresh-fx db)
                                        [:dispatch [:entity/clear {:note/uuid note-uuid}]])
                   :on-error-fx (conj (wall-refresh-fx db)
                                      [:dispatch [:toast/error {:text "Could not delete note. Try again."}]]))})

;; Apply the composer's staged payload to an existing note — one request
;; carrying the detaches and the upsert, whose response is the note as saved.
;; Diffs against the note as the entity store holds it now, not as the
;; composer was mounted with.
(defevent-fx ::save-edit [{:keys [db]} note-uuid staged]
  (let [ident (note-ident note-uuid)
        request-id [::save-edit (second ident)]]
    (when-not (request/in-flight? db request-id)
      {:db (request/mark-in-flight db request-id)
       :resolve (assoc (mutations/save-resolve {:note (get-in db ident)} staged)
                       :request-id request-id
                       :store-in ident
                       :on-success-fx (conj (wall-refresh-fx db)
                                            [:dispatch [:toast/success {:text "Note saved"}]])
                       :on-error-fx [[:dispatch [:toast/error {:text "Could not save note. Try again."}]]])})))

(def ^:private facets-eql
  [:facet/total
   ;:facet/no-web-page-count
   :facet/untagged-count
   :facet/no-contact-count
   {:facet/tags [:tag/name :facet/count]}
   {:facet/contacts [:contact/uuid :contact/full-name :facet/count]}
   #_{:facet/domains [:domain/name :facet/count
                    {:domain/urls [:url/value :url/label :facet/count]}]}])

(defn- live-filters
  "The wall's active filter values, read from the filters SUB-form's fields —
   not the root form's `:table/filters` map. A sub-field write lands in the
   sub-form's field state synchronously but reaches the root map only through
   a chain of queued events, so a facet-click handler dispatching
   [[::load-facets]] right after `set-value!` would read a stale root."
  [db]
  (into {}
        (keep (fn [[attr {current-value :form.field/current-value}]]
                (when (some? current-value)
                  [attr current-value])))
        (get-in db [:forms [table-id :table/filters] :form/fields])))

;; Facet option lists + SMART counts for the filter sidebar
;; (`hai.pathom.notes/user-note-facets`): each dimension's counts respect the
;; wall's live search + the OTHER dimensions' filters, sent as query params.
;; Loaded on sidebar mount, after note mutations, and after every filter or
;; (debounced) search change.
(defevent-fx ::load-facets
  [{:keys [db]} _]
  (let [active (live-filters db)
        search (get-in db [:forms table-id :form/fields
                           :table/search :form.field/current-value])
        params (cond-> {}
                 (seq active) (assoc :collection.filters/selected active)
                 (some-> search str/trim not-empty) (assoc :search search))]
    {:resolve {:query        [(if (seq params)
                                (list {:user/note-facets facets-eql} params)
                                {:user/note-facets facets-eql})]
               :transform-fn :user/note-facets
               :store-in     [::facets]}}))

;; Search keystrokes funnel through this so the counts re-query once typing
;; pauses — the same cadence as the table's own debounced resolve.
(defevent-fx ::load-facets-debounced
  [_ _]
  {:debounce/dispatch {:id ::load-facets
                       :event [::load-facets]
                       :duration 500}})

(defsub ::facets [db]
  (get db ::facets))
